import React, { useState } from 'react';
import { statusBadgeColor, statusBadgeText } from '../utils/BadgeEssay';
import moment from 'moment';
import { FiEdit3, FiEye, FiUpload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import { useClient } from 'jsonapi-react';
import usePermission from '@/app/hooks/usePermission';

export default function MobileEssayStudentCard({ student, essay, currentUser }) {
  const navigate = useNavigate();
  const statusClass = statusBadgeColor?.[student.status];
  const badgeText = statusBadgeText?.[student.status];
  const [openWarning, setOpenWarning] = useState(false);
  const client = useClient();

  const canSend = usePermission('Enviar redação', 'Redação');

  const handleReviewEssay = () => {
    if (student.status === 'unreviewed') {
      client.mutate(['essays', essay.id, 'essay_students', student.id], { status: 'reviewing' });
    }
    navigate(`/redacao/correcao/${student.id}/${essay.id}?modo=correcao`);
    setOpenWarning(false);
  };

  function renderAction() {
    const { status, id, corrector, created_by, user } = student;

    const viewEnabled =
      status === 'reviewed' || // corrigida pelo professor
      status === 'unreviewed' || // correção por IA
      status === 'annulled_by_teacher' || // anulada pelo professor
      status === 'reviewing' || // está sendo corrigida
      status === 'annulled'; // anulada

    const reviewEnabled =
      status === 'unreviewed' || // correção por IA
      (status === 'reviewing' && corrector.id === currentUser); // corrigindo e quem está corrigindo é o usuário atual

    const reachedSubmissionLimit = () => {
      const limit = essay['allow-multiple-submissions'] ? 2 : 1;
      return student['submission_order'] >= limit;
    };

    const isInvalid = () => ['annulled', 'annulled_by_teacher', 'minimum_length_not_satisfied'].includes(student.status);

    const sendDisabled =
      !canSend || // não tem permissão
      (reachedSubmissionLimit() && status !== 'ocr_review') || // já enviou a quantidade permitida
      status === 'reviewing' || // está sendo corrigida
      status === 'processing' || // está sendo processada
      (isInvalid() && reachedSubmissionLimit()) || // é inválida e já enviou a quantidade permitida
      status === 'close_for_submissions' || // prazo de envio encerrado
      essay.status === 'close_for_submissions' || // prazo de envio encerrado (status geral da redação)
      status === 'unreviewed'; // correção por IA

    return (
      <div className="flex w-full flex-col gap-2">
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => navigate(`/redacao/enviar/${essay.id}/${user?.id}`)}
          disabled={sendDisabled}
        >
          <FiUpload className="btn__icon" /> <span style={{ minWidth: '82px', fontSize: '12px' }}>Enviar redação</span>
        </button>
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => navigate(`/redacao/correcao/${id}/${essay.id}?modo=visualizar`)}
          disabled={!viewEnabled}
        >
          <FiEye className="btn__icon" /> <span style={{ fontSize: '12px' }}>Visualizar</span>
        </button>
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => {
            if (status === 'reviewing' && corrector.id === currentUser) {
              navigate(`/redacao/correcao/${id}/${essay.id}?modo=correcao`);
            } else {
              setOpenWarning(true);
            }
          }}
          disabled={!reviewEnabled}
        >
          <FiEdit3 className="btn__icon" /> <span style={{ fontSize: '12px' }}>Corrigir</span>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="card__container">
          <div className="card__content">
            <div className="card__header">
              <h2
                className="card__title mb-2"
                title={student?.user?.name}
              >
                {student?.user?.name}
              </h2>
              <span
                title={badgeText}
                className={`badge badge--tiny ${statusClass}`}
              >
                {badgeText}
              </span>
            </div>

            <div className="card__body">
              <div className="card__columns">
                <p className="card__count">
                  <span className="card__count-label">Entrega</span>
                  {student?.['delivery-date'] ? moment(student?.['delivery-date']).format('DD/MM/YYYY') : '-'}
                </p>
                <p className="card__count">
                  <span className="card__count-label">Nota</span>
                  {student?.grade ?? '-'}
                </p>
              </div>
              <p className="card__count">
                <span className="card__count-label">Corrigido por</span>
                {student?.corrector?.name ?? '-'}
              </p>
            </div>

            <div className="card__footer">{renderAction()}</div>
          </div>
        </div>
      </div>
      <ModalWarning
        show={openWarning}
        onClose={() => setOpenWarning(false)}
        titleFirstButton="Não"
        titleSecondButton="Sim"
        title={`Deseja mesmo corrigir essa redação?`}
        description="Outros professores não poderão corrigi-la."
        onSubmitFirstButton={() => setOpenWarning(false)}
        onSubmitSecondButton={() => handleReviewEssay()}
      />
    </>
  );
}
